<template>
  <div class="pill-tabs">
    <div ref="list" class="pill-tabs__list">
      <!-- On the track, not the buttons: a native-disabled button swallows
           mouse events, so a tooltip inside it would never trigger. -->
      <c-tooltip v-if="tooltip !== undefined">{{ tooltip }}</c-tooltip>

      <!-- The moving pill; slides to the active tab with a water-drop squash. -->
      <span
        class="pill-tabs__indicator"
        :class="{ 'pill-tabs__indicator--ready': ready }"
        :style="indicatorStyle"
      >
        <span
          class="pill-tabs__indicator-fill"
          :class="{ 'pill-tabs__indicator-fill--drop': dropping }"
        />
      </span>

      <button
        v-for="tab in tabs"
        :key="tab.name"
        :ref="(el) => registerEl(tab.name, el)"
        type="button"
        class="pill-tabs__tab"
        :class="{ 'pill-tabs__tab--active': tab.name === active }"
        :disabled="disable || tab.disable === true"
        @click="active = tab.name"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="panelTabs.length > 0" class="pill-tabs__panels">
      <!-- The inactive panel only collapses visually, so it stays keyboard- and
           screen-reader-reachable; inert + aria-hidden take it out of both. -->
      <div
        v-for="tab in panelTabs"
        :key="tab.name"
        class="pill-tabs__panel"
        :class="{ 'pill-tabs__panel--active': tab.name === active }"
        :inert="tab.name !== active"
        :aria-hidden="tab.name !== active"
      >
        <div class="pill-tabs__panel-inner">
          <slot :name="tab.name" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  useSlots,
  watch,
} from "vue";
import type { TabItem } from "./tabs.types";

// The active tab's name.
const active = defineModel<string | null>({ default: null });

const props = withDefaults(
  defineProps<{
    /** The tab row, in order. Each tab's panel is the slot with its name. */
    tabs: TabItem[];
    /**
     * Locks the whole switch: panels still render, but the tab cannot be
     * changed. A single entry locks itself through its own `disable`.
     */
    disable?: boolean;
    /** Shown when hovering the tab row — e.g. to explain a locked switch. */
    tooltip?: string;
  }>(),
  {
    disable: false,
    tooltip: undefined,
  },
);

const slots = useSlots();

// Only tabs with a matching named slot get a panel.
const panelTabs = computed(() =>
  props.tabs.filter((tab) => slots[tab.name] !== undefined),
);

// --- Sliding "water-drop" indicator -----------------------------------------
const list = ref<HTMLElement | null>(null);
const tabEls: Record<string, HTMLElement> = {};

const registerEl = (name: string, el: unknown): void => {
  if (el instanceof HTMLElement) {
    tabEls[name] = el;
  }

  if (el === null) {
    delete tabEls[name];
  }
};

// Position/size of the pill, tracked to the active tab button.
const indicator = reactive({ x: 0, width: 0, top: 0, height: 0 });
const ready = ref(false); // no transition on the very first placement
const dropping = ref(false); // replays the squash on each move

const indicatorStyle = computed(() => ({
  transform: `translateX(${indicator.x}px)`,
  width: `${indicator.width}px`,
  top: `${indicator.top}px`,
  height: `${indicator.height}px`,
}));

const measure = (): void => {
  const el = active.value === null ? undefined : tabEls[active.value];

  if (el === undefined) {
    return;
  }

  indicator.x = el.offsetLeft;
  indicator.width = el.offsetWidth;
  indicator.top = el.offsetTop;
  indicator.height = el.offsetHeight;
};

watch(active, async () => {
  measure();

  // Restart the squash animation on every switch.
  dropping.value = false;
  await nextTick();
  dropping.value = true;
});

// Default to the first tab, and re-measure when the tab row changes.
watch(
  () => props.tabs,
  async (tabs) => {
    if (active.value === null && tabs.length > 0) {
      active.value = tabs[0].name;
    }

    await nextTick();
    measure();
  },
  { immediate: true },
);

// The pill is positioned/sized from the active tab's measured offsets, so it
// must be re-measured whenever the track's width changes — e.g. a scrollbar
// appearing in the modal shrinks it, otherwise the pill keeps the old (wider)
// size and clips outside the track.
let resizeObserver: ResizeObserver | null = null;

onMounted(async () => {
  await nextTick();
  measure();

  // Enable the slide transition only after the pill is first placed.
  await nextTick();
  ready.value = true;

  if (list.value !== null) {
    resizeObserver = new ResizeObserver(() => measure());
    resizeObserver.observe(list.value);
  }
});

onBeforeUnmount(() => resizeObserver?.disconnect());
</script>

<style lang="scss" scoped>
.pill-tabs {
  @apply tw-flex tw-flex-col tw-gap-6;
}

// Segmented track: 40px tall, 32px pill inside.
.pill-tabs__list {
  @apply tw-relative tw-flex tw-h-auto tw-min-h-10 tw-items-center tw-gap-1 tw-overflow-x-auto tw-overflow-y-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50 tw-px-1;
}

// The pill sits behind the labels and slides between tabs.
.pill-tabs__indicator {
  @apply tw-pointer-events-none tw-absolute tw-left-0 tw-z-0;

  &--ready {
    transition:
      transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1),
      width 350ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }
}

.pill-tabs__indicator-fill {
  @apply tw-block tw-h-full tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-shadow-sm;
  transform-origin: center;

  // Squash-and-stretch on each move for the water-drop feel.
  &--drop {
    animation: pill-drop 350ms ease;
  }
}

@keyframes pill-drop {
  0% {
    transform: scale(1, 1);
  }

  40% {
    transform: scale(1.08, 0.82);
  }

  70% {
    transform: scale(0.98, 1.06);
  }

  100% {
    transform: scale(1, 1);
  }
}

.pill-tabs__tab {
  @apply tw-relative tw-z-10 tw-flex tw-h-8 tw-flex-1 tw-items-center tw-justify-center tw-rounded-lg tw-px-4 tw-text-sm tw-font-medium tw-text-gray-500 tw-transition-colors;

  &:not(:disabled) {
    @apply tw-cursor-pointer hover:tw-bg-white/60 hover:tw-text-gray-900;
  }

  &--active {
    @apply tw-text-gray-900;
  }

  &:disabled {
    @apply tw-cursor-default;
  }
}

// The inactive panel collapses to 0 height (animated via the grid-rows trick),
// so switching tabs morphs the height smoothly instead of snapping.
.pill-tabs__panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 300ms ease-in-out;

  &--active {
    grid-template-rows: 1fr;
  }
}

.pill-tabs__panel-inner {
  @apply tw-overflow-hidden;
  min-height: 0;
}
</style>
